import { useSubscription } from "@apollo/client";
import { Space, Typography } from "antd";
import { useState, useEffect } from "react";

import { formatDate } from "@src/utils/dateHelpers";

import { mapEdgeStateErrorStateToStatus } from "./utils";
import { EdgeStateDocument, EdgeStateErrorState } from "../../graphql/graphql";
import StatusTag from "../StatusTag/StatusTag";

const { Text } = Typography;

/**
 * Displays a StatusTag of the error state from the edge state.
 */
const EdgeStateStatus = ({
  assetId,
  lastOnlineAt,
  latestEdgeServiceVersion,
  statusOnly,
}: {
  assetId: string;
  statusOnly?: boolean;
  lastOnlineAt?: string | null | undefined;
  latestEdgeServiceVersion?: string | undefined;
}) => {
  const subscription = useSubscription(EdgeStateDocument, {
    variables: { assetId },
  });
  const [edgeStateErrorState, setEdgeStateErrorState] =
    useState<EdgeStateErrorState>(EdgeStateErrorState.Unrecognized);
  const [localLastOnlineAt, setLastOnlineAt] = useState<
    Date | string | undefined | null
  >(undefined);
  const [localLatestEdgeServiceVersion, setLatestEdgeServiceVersion] = useState<
    string | undefined
  >(undefined);

  // Set the last online at time and edge service version
  // if it hasn't been set yet as soon as it's loaded from the asset.
  if (localLastOnlineAt === undefined && lastOnlineAt !== undefined) {
    setLastOnlineAt(lastOnlineAt);
  }
  if (
    localLatestEdgeServiceVersion === undefined &&
    latestEdgeServiceVersion !== undefined
  ) {
    setLatestEdgeServiceVersion(latestEdgeServiceVersion);
  }

  const status = mapEdgeStateErrorStateToStatus(edgeStateErrorState);

  useEffect(() => {
    const edgeState = subscription.data?.edgeState;
    if (edgeState) {
      setEdgeStateErrorState(edgeState.errorState);
      setLastOnlineAt(new Date());
      setLatestEdgeServiceVersion(edgeState.version);
    }
  }, [subscription]);

  const showAdditionalInfo = !(statusOnly ?? false);

  return (
    <>
      <StatusTag status={status} />

      {showAdditionalInfo && (
        <Space direction="horizontal" size="large">
          <Text type="secondary">
            Last Online:{" "}
            {localLastOnlineAt ? formatDate(localLastOnlineAt) : "N/A"}
          </Text>
          <Text type="secondary">
            Latest Version: {localLatestEdgeServiceVersion ?? "N/A"}
          </Text>
        </Space>
      )}
    </>
  );
};

export default EdgeStateStatus;
